import React from "react";
import { ToastContainer, toast } from "react-toastify";
import { PaymentApis } from "../../apis/paymentApis";
import {
  FiInfo, FiArrowUpCircle, FiArrowDownCircle, FiClock,
  FiX, FiDollarSign, FiChevronLeft, FiChevronRight, FiInbox,
} from "react-icons/fi";

const fmt = (n) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(Number(n) || 0);

const BANKS = [
  { code: "044", name: "Access Bank" },
  { code: "011", name: "First Bank" },
  { code: "058", name: "Guaranty Trust Bank (GTB)" },
  { code: "035", name: "Wema Bank" },
  { code: "033", name: "United Bank for Africa (UBA)" },
  { code: "560", name: "Page MFBank" },
  { code: "304", name: "Stanbic Mobile Money" },
  { code: "050", name: "Ecobank Plc" },
  { code: "307", name: "EcoMobile" },
  { code: "309", name: "FBNMobile" },
  { code: "326", name: "Sterling Mobile" },
  { code: "057", name: "Zenith Bank" },
  { code: "068", name: "Standard Chartered Bank" },
  { code: "070", name: "Fidelity Bank" },
  { code: "215", name: "Unity Bank" },
  { code: "323", name: "Access Money" },
  { code: "318", name: "Fidelity Mobile" },
  { code: "221", name: "Stanbic IBTC Bank" },
  { code: "322", name: "ZenithMobile" },
  { code: "063", name: "Diamond Bank" },
  { code: "030", name: "Heritage" },
  { code: "032", name: "Union Bank" },
  { code: "232", name: "Sterling Bank" },
  { code: "076", name: "Skye Bank" },
  { code: "082", name: "Keystone Bank" },
  { code: "327", name: "Pagatech" },
  { code: "313", name: "Mkudi" },
  { code: "214", name: "First City Monument Bank" },
  { code: "315", name: "GTMobile" },
];

export default function CardPaymentDashboard() {
  const [visible, setVisible] = React.useState(false);
  const [balance, setBalance] = React.useState({});
  const [transactionList, setTransactionList] = React.useState([]);
  const [lastWithdrawal, setLastWithdrawal] = React.useState({});
  const [lastDeposit, setLastDeposit] = React.useState({});
  const [refresh, setRefresh] = React.useState(false);
  const [loader, setLoader] = React.useState(true);
  const [bankCode, setBankCode] = React.useState("");
  const [accountNumber, setAccountNumber] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    setLoader(true);
    PaymentApis.getTransactions().then((res) => {
      if (res?.data) {
        setTransactionList(res.data.data.transactions);
        setBalance(res.data.data.walletDetails || {});
        setLastWithdrawal(res.data.data.witdrawal || {});
        setLastDeposit(res.data.data.deposit || {});
      }
    }).finally(() => setLoader(false));
  }, [refresh]);

  const paginator = React.useCallback((page) => {
    PaymentApis.getTransactions(page ?? "").then((res) => {
      if (res?.data) {
        setTransactionList(res.data.data.transactions);
        setBalance(res.data.data.walletDetails || {});
      }
    }).catch(() => {});
  }, []);

  const handleWithdraw = React.useCallback(async (e) => {
    e.preventDefault();
    if (parseInt(balance?.total_amount) < parseInt(amount)) {
      toast.error("Insufficient funds.");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("account_bank", bankCode);
    fd.append("account_number", accountNumber);
    fd.append("amount", amount);
    try {
      const res = await PaymentApis.requestWitdrawal(fd);
      if (res?.data) {
        toast.success(res.data.message);
        setVisible(false);
        setRefresh((r) => !r);
      } else {
        toast.error(res?.response?.data?.message || "Withdrawal failed.");
        setVisible(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong.");
      setVisible(false);
    } finally {
      setSubmitting(false);
    }
  }, [bankCode, accountNumber, amount, balance]);

  React.useEffect(() => {
    document.body.style.overflow = visible ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [visible]);

  const rows = transactionList?.data?.filter((d) => d?.role !== "admin") || [];

  return (
    <div className="bg-gray-50 min-h-screen pb-12">

      {/* Alert */}
      <div className="flex items-start gap-2.5 bg-blue-50 border-b border-blue-100 px-4 py-2.5">
        <FiInfo className="text-blue-400 flex-shrink-0 mt-0.5" size={14} />
        <p className="text-xs text-blue-600 leading-snug">
          Dear vendors, your trust is crucial. Any suspicious activity or abnormal transactions may lead to an account lock. We're committed to maintaining a secure platform for everyone.
        </p>
      </div>

      <div className="px-3 sm:px-5 pt-5 space-y-4">

        {/* ── Wallet card ──────────────────────────────────────── */}
        <div
          className="rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          style={{ background: "linear-gradient(135deg, #0071BC 0%, #024471 100%)" }}
        >
          <div>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>
              Gupta Wallet Balance
            </p>
            {loader
              ? <div className="h-9 w-44 rounded-lg" style={{ backgroundColor: "rgba(255,255,255,0.12)" }} />
              : <p style={{ color: "#ffffff", fontSize: "32px", fontWeight: 800, lineHeight: 1 }}>{fmt(balance?.total_amount)}</p>
            }
          </div>
          <button
            onClick={() => setVisible(true)}
            className="self-start sm:self-auto flex-shrink-0 bg-white text-[#0071BC] text-sm font-bold px-6 py-2.5 rounded-xl hover:bg-blue-50 active:scale-95 transition-all"
          >
            Withdraw Funds
          </button>
        </div>

        {/* ── Stat cards ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: "Previous Balance",  value: fmt(balance?.previous_amount),  icon: <FiClock size={16} className="text-gray-400" />,  bg: "#f3f4f6" },
            { label: "Last Withdrawal",   value: fmt(lastWithdrawal?.amount),     icon: <FiArrowUpCircle size={16} className="text-red-400" />,   bg: "#fff1f0" },
            { label: "Last Deposit",      value: fmt(lastDeposit?.amount_paid),   icon: <FiArrowDownCircle size={16} className="text-green-500" />, bg: "#f0fdf4" },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-2xl px-5 py-4 flex items-center gap-4 shadow-sm">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.bg }}>
                {s.icon}
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">{s.label}</p>
                {loader
                  ? <div className="mt-1 h-4 w-24 bg-gray-100 rounded animate-pulse" />
                  : <p className="text-sm font-extrabold text-gray-800 mt-0.5">{s.value}</p>
                }
              </div>
            </div>
          ))}
        </div>

        {/* ── Transaction History ───────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-extrabold text-gray-900">Transaction History</h2>
              <p className="text-xs text-gray-400 mt-0.5">Payments received from customers</p>
            </div>
            {rows.length > 0 && (
              <span className="text-xs font-bold text-[#0071BC] bg-blue-50 px-3 py-1 rounded-full">
                {rows.length} record{rows.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {loader ? (
            <div className="divide-y divide-gray-50">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="px-5 py-3.5 flex gap-6">
                  {[32, 40, 48, 28, 20, 24].map((w) => (
                    <div key={w} className={`h-3.5 bg-gray-100 rounded-md animate-pulse`} style={{ width: `${w * 3}px` }} />
                  ))}
                </div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                <FiInbox className="text-gray-300" size={22} />
              </div>
              <p className="text-sm font-semibold text-gray-400">No transactions yet</p>
              <p className="text-xs text-gray-300 mt-1">Customer payments will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {["#", "Customer", "Email", "Phone", "Paid For", "Amount", "Qty"].map((h) => (
                      <th key={h} className="px-5 py-3 text-xs font-bold text-gray-400 text-left uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50/70 transition-colors">
                      <td className="px-5 py-3.5 text-xs text-gray-300 font-mono">{i + 1}</td>
                      <td className="px-5 py-3.5 font-semibold text-gray-800 whitespace-nowrap">{row?.customer_name || "—"}</td>
                      <td className="px-5 py-3.5 text-gray-500 whitespace-nowrap">{row?.customer_email || "—"}</td>
                      <td className="px-5 py-3.5 text-gray-500 whitespace-nowrap">{row?.customer_phone_number || "—"}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="px-2.5 py-1 bg-blue-50 text-[#0071BC] text-xs font-bold rounded-lg">{row?.paying_for || "—"}</span>
                      </td>
                      <td className="px-5 py-3.5 font-bold text-gray-900 whitespace-nowrap">{fmt(row?.amount_paid)}</td>
                      <td className="px-5 py-3.5 text-gray-500">{row?.product_qty ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {transactionList?.links?.length > 0 && (
            <div className="px-5 py-3.5 border-t border-gray-100 flex items-center gap-2 flex-wrap">
              {transactionList.links.map((link, i) => {
                const label = link.label === "&laquo; Previous" ? "prev" : link.label === "Next &raquo;" ? "next" : link.label;
                const page = link.url ? link.url.charAt(link.url.length - 1) : null;
                return (
                  <button
                    key={i}
                    onClick={() => paginator(page)}
                    disabled={link.active}
                    className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-xl transition-all ${link.active ? "bg-[#0071BC] text-white cursor-default" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                  >
                    {label === "prev" ? <><FiChevronLeft size={11} /> Prev</> : label === "next" ? <>Next <FiChevronRight size={11} /></> : label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Withdrawal Modal ─────────────────────────────────────── */}
      {visible && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)" }}
        >
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FiDollarSign className="text-[#0071BC]" size={16} />
                </div>
                <div>
                  <p className="text-sm font-extrabold text-gray-900">Withdraw Funds</p>
                  <p className="text-xs text-gray-400">Balance: {fmt(balance?.total_amount)}</p>
                </div>
              </div>
              <button onClick={() => setVisible(false)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 text-gray-400 hover:bg-gray-200 transition-colors">
                <FiX size={14} strokeWidth={2.5} />
              </button>
            </div>

            <form onSubmit={handleWithdraw} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Select Bank</label>
                <select required onChange={(e) => setBankCode(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all">
                  <option value="" disabled selected>Choose bank…</option>
                  {BANKS.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Account Number</label>
                <input required type="number" placeholder="0000000000"
                  onChange={(e) => setAccountNumber(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Amount (NGN)</label>
                <input required type="number" placeholder="e.g. 20000"
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all" />
                {amount && parseInt(amount) > parseInt(balance?.total_amount || 0) && (
                  <p className="text-xs text-red-500 mt-1.5 font-semibold">Exceeds available balance.</p>
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setVisible(false)}
                  className="flex-1 py-3 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="flex-1 py-3 bg-[#0071BC] text-white text-sm font-extrabold rounded-2xl hover:bg-[#005fa0] active:scale-[0.97] transition-all disabled:opacity-60">
                  {submitting ? "Processing…" : "Proceed"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar theme="light" toastStyle={{ borderRadius: "12px" }} />
    </div>
  );
}
